<template>
  <!--
    A right-side drawer (AB#21838), like every other order pane: the order and
    its timeline stay visible beside the conversations the threads refer to.
    Wide, because an email is wide. The shell (below the app bar, scrim, ×,
    Esc, tokens, Ask Atlas clearance) is the shared Drawer.
  -->
  <Drawer
    :title="`Order Communication — Order #${orderNo}`"
    width="min(100vw, 860px)"
    data-test="zendesk-modal"
    @close="close"
  >
    <template #subhead>
      <div class="zd-sub">
        <span class="text-xs text-ink-mute">{{ fetchedLine }}</span>
        <button
          type="button"
          class="zd-refresh"
          :disabled="isLoading"
          data-test="zendesk-refresh"
          @click="reload"
        >
          Refresh
        </button>
      </div>
    </template>

    <div>
      <p v-if="error" class="zd-error text-sm px-3 py-2 rounded" data-test="zendesk-error">
        {{ error }}
      </p>

      <UnderlineTabs
        :key="zendeskOff ? 'wa-first' : 'tickets-first'"
        :tabs="tabs"
        :initialTab="zendeskOff && whatsappAllowed && orderItemId ? 'whatsapp' : activeTab"
        @change="onTab"
      >
        <!-- `failed` suppresses the empty state, and is not the same thing as
                 an empty list. "No customer conversations found for this order"
                 is a finding, and we only have it when the lookup came back. On a
                 failed load we know nothing about this order's conversations, and
                 printing that sentence under the error banner told the reader the
                 opposite of what the banner said — the two lines contradicted each
                 other, and the reassuring one was the false one. -->
        <!-- AB#21837 — the modal opens for every merchant now, for WhatsApp.
                 Zendesk itself is still connected for Hummel Pro NA only; where
                 it is not, the two ticket tabs say so instead of "none found". -->
        <template #customer>
          <div v-if="zendeskOff" class="zendesk-soon" data-test="zendesk-unsupported-customer">
            <p class="zendesk-soon-title">Zendesk is not connected for this merchant</p>
            <p class="zendesk-soon-text">
              Email conversations show here for Hummel Pro NA orders. WhatsApp messages are on the
              WhatsApp tab.
            </p>
          </div>
          <TicketList
            v-else
            :tickets="customer"
            :loading="isLoading"
            :failed="Boolean(error)"
            empty="No customer conversations found for this order."
            kind="customer"
            :order-id="orderId"
            @confirmed="onConfirmed"
          />
        </template>
        <template #factory>
          <div v-if="zendeskOff" class="zendesk-soon" data-test="zendesk-unsupported-factory">
            <p class="zendesk-soon-title">Zendesk is not connected for this merchant</p>
            <p class="zendesk-soon-text">
              Email conversations show here for Hummel Pro NA orders. WhatsApp messages are on the
              WhatsApp tab.
            </p>
          </div>
          <TicketList
            v-else
            :tickets="factory"
            :loading="isLoading"
            :failed="Boolean(error)"
            empty="No factory conversations found for this order."
            kind="factory"
            :order-id="orderId"
            @confirmed="onConfirmed"
          />
        </template>
        <!-- AB#21837 — WhatsApp, live: the feed's messages for this order, on
                 the Ask ELSA gate (third-party data, internal desk). -->
        <template #whatsapp>
          <WhatsAppPanel
            v-if="whatsappAllowed && orderItemId && whatsapp"
            :order-item-id="orderItemId"
            :order-no="orderNo"
            :thread="whatsapp"
          />
          <div v-else class="zendesk-soon" data-test="zendesk-whatsapp-off">
            <p class="zendesk-soon-title">WhatsApp conversations are for the internal desk</p>
            <p class="zendesk-soon-text">Ask an administrator if you need to see them.</p>
          </div>
        </template>
      </UnderlineTabs>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, onMounted, PropType, ref, VNode } from 'vue';
import { messageForError } from '@/utils/userMessage';
import UnderlineTabs from '@/components/Shared/UnderlineTabs.vue';
import Drawer from '@/components/Shared/Drawer.vue';
import WhatsAppPanel from '@/components/Dashboard/Order/components/WhatsAppPanel.vue';
import { useWhatsAppThread } from '@/composables/useWhatsAppThread';
import { elsaAllowed } from '@/utils/elsaAccess';
import { useMainStore } from '@/store';
import {
  confirmMatch,
  rejectMatch,
  undoRejectMatch,
  formatBytes,
  formatDateTime,
  personLabel,
  sanitizeHtml,
  statusClass,
  useZendeskTickets,
  type MatchConfirmation,
  type MatchRejection,
} from '@/composables/useZendeskTickets';
import { showToast } from '@/composables/swal';
import { OrderCommunicationTab, ZendeskComment, ZendeskTab, ZendeskTicket } from '@/models';
import {
  CheckCircleIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps<{
  orderId: number;
  orderNo: string;
  /** AB#21837 — the WhatsApp panel is scoped per order item, like Ask ELSA. */
  orderItemId?: number;
}>();

const store = useMainStore();
const whatsappAllowed = computed(() => elsaAllowed(store.user?.roles, store.features));
// AB#21841 — the thread is loaded with the drawer so the tab can say how many
// messages there are before anyone clicks it; the panel reuses this instance.
const whatsapp =
  whatsappAllowed.value && props.orderItemId ? useWhatsAppThread(props.orderItemId) : null;
const whatsappCount = computed(() => {
  const d = whatsapp?.data.value;
  if (!d || !d.available) return null;
  return d.messages.filter((m) => !m.context).length;
});

const emit = defineEmits<{ (e: 'close'): void }>();

const { data, customer, factory, counts, isLoading, error, load } = useZendeskTickets();

const activeTab = ref<OrderCommunicationTab>('customer');
const fetchedLine = computed(() =>
  isLoading.value
    ? 'Looking up conversations…'
    : data.value.fetched_at
      ? `Fetched ${formatDateTime(data.value.fetched_at)} · read-only, reply from the full ticket`
      : ''
);
/** Zendesk answered "not enabled for this order" (another merchant). */
const zendeskOff = computed(
  () => !isLoading.value && !error.value && data.value?.supported === false
);
// The count is dropped when the lookup failed, for the same reason the empty
// state is: a bracketed 0 beside an error is a measurement nobody took. The tab
// keeps its name so the modal does not reshuffle while the user reads the banner.
const tabs = computed(() => [
  {
    id: 'customer',
    label: error.value || zendeskOff.value ? 'Customer' : `Customer (${counts.value.customer})`,
  },
  {
    id: 'factory',
    label: error.value || zendeskOff.value ? 'Factory' : `Factory (${counts.value.factory})`,
  },
  {
    id: 'whatsapp',
    label: whatsappCount.value === null ? 'WhatsApp' : `WhatsApp (${whatsappCount.value})`,
  },
]);
const onTab = (id: string) => {
  activeTab.value = id as OrderCommunicationTab;
};

const close = () => emit('close');
const reload = () => load(props.orderId, true);

/**
 * AB#21284 — a person confirmed a reference-only match. The backend has
 * written the note on the ticket; reloading fresh brings that note back as a
 * message in the thread, and the thread now names the order, so the amber
 * line goes and the ticket reads as matched by order number — the same way
 * it will read for everyone from now on.
 */
const onConfirmed = (confirmation: MatchConfirmation) => {
  showToast(
    `Noted on ticket #${confirmation.ticket_id}: this is order #${confirmation.order_no}.`,
    'success'
  );
  void load(props.orderId, true);
};

onMounted(() => {
  // When Zendesk turns out to be off for this merchant, the tabs remount (the
  // :key above) opening on WhatsApp — the one tab with something to read.
  load(props.orderId);
  void whatsapp?.load();
});

// ---------------------------------------------------------------- ticket list (render function keeps this file self-contained)

const TicketList = defineComponent({
  name: 'ZendeskTicketList',
  props: {
    tickets: { type: Array as PropType<ZendeskTicket[]>, required: true },
    loading: { type: Boolean, default: false },
    /** The lookup failed, so an empty list is "unknown", not "none". */
    failed: { type: Boolean, default: false },
    empty: { type: String, default: 'Nothing here.' },
    kind: { type: String as PropType<ZendeskTab>, required: true },
    /** The order this panel is for; the confirm call needs it (AB#21284). */
    orderId: { type: [Number, String] as PropType<number | string>, required: true },
  },
  emits: ['confirmed'],
  setup(p, { emit }) {
    const expanded = ref<Set<number>>(new Set());
    /*
     * AB#21284 — confirming a reference-only match. `confirming` while the
     * note is being written; `confirmed` once it is, until the fresh reload
     * replaces this list with one where the thread names the order.
     */
    const confirming = ref<Set<number>>(new Set());
    const confirmed = ref<Map<number, MatchConfirmation>>(new Map());
    const confirm = async (t: ZendeskTicket) => {
      if (confirming.value.has(t.id) || confirmed.value.has(t.id)) return;
      confirming.value = new Set(confirming.value).add(t.id);
      try {
        const result = await confirmMatch(
          p.orderId,
          t.id,
          t.subject ? `customer reference "${t.subject}"` : undefined
        );
        confirmed.value = new Map(confirmed.value).set(t.id, result);
        emit('confirmed', result);
      } catch (e) {
        showToast(
          messageForError(e, "The note couldn't be added to the ticket. Try again."),
          'error'
        );
      } finally {
        const next = new Set(confirming.value);
        next.delete(t.id);
        confirming.value = next;
      }
    };
    /*
     * The other answer. A rejected ticket is NOT reloaded away: it stays on
     * screen in its grey state with an Undo until the next load, when the
     * backend leaves it out of this order's thread for good.
     */
    const rejecting = ref<Set<number>>(new Set());
    const rejected = ref<Map<number, MatchRejection>>(new Map());
    const reject = async (t: ZendeskTicket) => {
      if (rejecting.value.has(t.id) || rejected.value.has(t.id) || confirmed.value.has(t.id))
        return;
      rejecting.value = new Set(rejecting.value).add(t.id);
      try {
        const result = await rejectMatch(p.orderId, t.id);
        rejected.value = new Map(rejected.value).set(t.id, result);
        showToast(
          `Noted on ticket #${t.id}: not order #${props.orderNo}. It won't show under this order again.`,
          'success'
        );
      } catch (e) {
        showToast(
          messageForError(e, "The note couldn't be added to the ticket. Try again."),
          'error'
        );
      } finally {
        const next = new Set(rejecting.value);
        next.delete(t.id);
        rejecting.value = next;
      }
    };
    const undoReject = async (t: ZendeskTicket) => {
      if (rejecting.value.has(t.id) || !rejected.value.has(t.id)) return;
      rejecting.value = new Set(rejecting.value).add(t.id);
      try {
        await undoRejectMatch(p.orderId, t.id);
        const next = new Map(rejected.value);
        next.delete(t.id);
        rejected.value = next;
        showToast(
          `Undone. Ticket #${t.id} can show under order #${props.orderNo} again.`,
          'success'
        );
      } catch (e) {
        showToast(messageForError(e, "This couldn't be undone on the ticket. Try again."), 'error');
      } finally {
        const next = new Set(rejecting.value);
        next.delete(t.id);
        rejecting.value = next;
      }
    };
    const showHtml = ref<Set<number>>(new Set());
    const toggle = (id: number) => {
      const next = new Set(expanded.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      expanded.value = next;
    };
    const toggleHtml = (id: number) => {
      const next = new Set(showHtml.value);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      showHtml.value = next;
    };

    const renderComment = (c: ZendeskComment): VNode =>
      h(
        'div',
        {
          key: c.id,
          // Internal notes use the themed warning surface, not literal amber:
          // `bg-amber-50` renders near-white in dark mode, which is why the
          // note body looked unstyled there. `--warning-soft` is #fff4e5 in
          // light (≈ the old amber-50) and a dark brown in dark.
          class: [
            'rounded-md border px-3 py-2 text-sm',
            c.public ? 'border-line bg-surface' : 'border-line bg-[var(--warning-soft)]',
          ],
          'data-test': 'zendesk-comment',
        },
        [
          h('div', { class: 'flex flex-wrap items-center gap-x-2 text-xs text-ink-mute mb-1' }, [
            h('span', { class: 'font-medium text-ink' }, personLabel(c.author)),
            c.author?.email ? h('span', `<${c.author.email}>`) : null,
            h('span', '·'),
            h('span', formatDateTime(c.created_at)),
            !c.public
              ? h(
                  'span',
                  {
                    class: 'ml-1 rounded bg-[var(--warning)] text-[var(--surface)] px-1.5 py-0.5',
                    'data-test': 'zendesk-internal',
                  },
                  'Internal note'
                )
              : null,
          ]),
          !showHtml.value.has(c.id) && c.html_body
            ? h('div', {
                class: 'zendesk-html prose prose-sm max-w-none',
                innerHTML: sanitizeHtml(c.html_body),
              })
            : h('div', { class: 'whitespace-pre-wrap break-words text-ink' }, c.plain_body || ''),
          c.html_body && c.html_body !== c.plain_body
            ? h(
                'button',
                {
                  class: 'mt-1 text-xs text-primary hover:underline',
                  type: 'button',
                  onClick: () => toggleHtml(c.id),
                },
                !showHtml.value.has(c.id) ? 'Show plain text' : 'Show formatted'
              )
            : null,
          c.attachments.length
            ? h(
                'ul',
                { class: 'mt-2 flex flex-wrap gap-2' },
                c.attachments.map((a) =>
                  h('li', { key: a.id }, [
                    h(
                      'a',
                      {
                        href: a.url,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        class:
                          'inline-flex items-center gap-1 rounded border border-line bg-surface-2 px-2 py-1 text-xs text-ink-soft hover:bg-surface-2',
                        'data-test': 'zendesk-attachment',
                      },
                      `📎 ${a.name} (${formatBytes(a.size)})`
                    ),
                  ])
                )
              )
            : null,
        ]
      );

    /**
     * Whether this thread names the order number anywhere in it (AB#21116).
     *
     * This is a substring search over the subject and the message bodies —
     * nothing more. It is not a score, not a judgement, and nothing to do with
     * ELSA: the thread either quotes the order number or it does not, and when
     * it does not, the thread was matched on the customer reference alone and
     * a person needs to confirm it is the right one.
     *
     * Extracted because it was computed three times in the same render — for
     * the class, the title and the text — and the three copies would drift.
     */
    const namesTheOrder = (t: ZendeskTicket): boolean => {
      if (!props.orderNo) {
        return false;
      }

      const haystack = [
        t.subject || '',
        ...(t.comments || []).map((c) => `${c.plain_body || ''} ${c.html_body || ''}`),
      ].join(' ');

      // As a whole number, not a substring: 27925 is not named by a thread
      // that mentions 279250. The server applies the same rule before it
      // writes a confirmation (OrderTicketMatcher::namesOrder), so the two
      // must agree on what "names the order" means.
      const escaped = props.orderNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|\\D)${escaped}(\\D|$)`).test(haystack);
    };

    const renderTicket = (t: ZendeskTicket): VNode => {
      const open = !expanded.value.has(t.id);
      // AB#21991 — the server also accepts the order's tech pack in the thread,
      // or a person's earlier confirmation, as proof the ticket is this order's.
      const vouched = t.link?.state === 'techpack' || t.link?.state === 'confirmed';
      const matched = namesTheOrder(t) || vouched;
      const matchLabel =
        t.link?.state === 'confirmed' && !namesTheOrder(t)
          ? `Confirmed as order #${props.orderNo}`
          : t.link?.state === 'techpack' && !namesTheOrder(t)
            ? `Carries the tech pack of order #${props.orderNo}`
            : `Names order #${props.orderNo}`;
      const others = t.link?.other_orders ?? [];
      return h(
        'div',
        {
          key: t.id,
          class: 'rounded-md border border-line mb-3',
          'data-test': `zendesk-ticket-${p.kind}`,
        },
        [
          h(
            'div',
            {
              class:
                'flex items-start justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-surface-2',
              onClick: () => toggle(t.id),
              'data-test': 'zendesk-ticket-toggle',
            },
            [
              h('div', { class: 'min-w-0' }, [
                h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
                  h(
                    'span',
                    {
                      class: `rounded px-2 py-0.5 text-xs font-medium uppercase ${statusClass(t.status)}`,
                      'data-test': 'zendesk-status',
                    },
                    t.status
                  ),
                  h(
                    'span',
                    { class: 'font-medium text-ink truncate' },
                    t.subject || '(no subject)'
                  ),
                  h(
                    'span',
                    {
                      class:
                        'text-xs font-semibold text-ink-soft bg-surface-2 rounded px-1.5 py-0.5 whitespace-nowrap',
                    },
                    `Ticket #${t.id}`
                  ),
                ]),
                /*
                 * How this thread was matched to the order.
                 *
                 * It used to be a green "Confirmed" / amber "Probable" chip sitting
                 * beside the Zendesk status, which produced rows reading
                 * "PENDING … Confirmed" — two badges, two different subjects, and
                 * nothing on screen saying which was which. Confirmed WHAT, by whom?
                 *
                 * Now it says what the match was actually based on, in the metadata
                 * line where the rest of the provenance lives. The amber case keeps
                 * its colour because it is the one that asks something of the
                 * reader; the strong case is quiet, because a correct match is not
                 * news.
                 */
                h('div', { class: 'text-xs mt-1' }, [
                  matched
                    ? h(
                        'span',
                        { class: 'text-ink-mute', 'data-test': 'zendesk-match' },
                        matchLabel
                      )
                    : null,
                  h(
                    'span',
                    { class: 'text-ink-mute' },
                    [
                      matched ? ' · ' : '',
                      `${p.kind === 'factory' ? 'Raised by' : 'From'} ${personLabel(t.requester)}`,
                      t.assignee ? ` · Assigned to ${personLabel(t.assignee)}` : '',
                      ` · ${t.comments.length} message${t.comments.length === 1 ? '' : 's'}`,
                      ` · Updated ${formatDateTime(t.updated_at)}`,
                    ].join('')
                  ),
                ]),
                // A reorder often points at the conversation of the order it
                // repeats: say so, or its sentences read as being about this one.
                others.length
                  ? h(
                      'div',
                      { class: 'text-xs mt-1 text-ink-mute', 'data-test': 'zendesk-other-orders' },
                      `Also about ${others.length === 1 ? 'order' : 'orders'} ${others
                        .map((n) => `#${n}`)
                        .join(', ')}`
                    )
                  : null,
              ]),
              h('div', { class: 'flex items-center gap-3 shrink-0' }, [
                h(
                  'a',
                  {
                    href: t.url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    class: 'text-sm text-primary hover:underline whitespace-nowrap',
                    'data-test': 'zendesk-open',
                    onClick: (e: Event) => e.stopPropagation(),
                  },
                  'Open full ticket ↗'
                ),
                h('span', { class: 'text-ink-mute text-sm' }, open ? '▲' : '▼'),
              ]),
            ]
          ),
          matched ? null : renderMatchCallout(t),
          open
            ? h(
                'div',
                {
                  class: 'border-t border-line-soft px-4 py-3 flex flex-col gap-2 bg-surface-2/40',
                  'data-test': 'zendesk-thread',
                },
                t.comments.map((c) => renderComment(c))
              )
            : null,
        ]
      );
    };

    /*
     * AB#21284 — the question a reference-only match asks, and its answer.
     *
     * This used to be an amber sentence in the metadata line with the button
     * inline after it, styled like the words around it — so it read as one
     * more label, and nobody clicked. It is its own row now, under the
     * header: a callout that says what is being asked and has one real
     * button to answer it. Once answered, the same row says who confirmed.
     */
    const renderMatchCallout = (t: ZendeskTicket): VNode => {
      const done = confirmed.value.get(t.id);
      if (done) {
        return h(
          'div',
          {
            class: 'zendesk-callout zendesk-callout--ok',
            'data-test': 'zendesk-match',
            role: 'status',
          },
          [
            h(CheckCircleIcon, { class: 'zendesk-callout-icon', 'aria-hidden': 'true' }),
            h('span', { class: 'zendesk-callout-text' }, [
              h('strong', `Confirmed as order #${props.orderNo}`),
              ` by ${done.confirmed_by || 'you'} — noted on the ticket.`,
            ]),
          ]
        );
      }
      const no = rejected.value.get(t.id);
      if (no) {
        return h(
          'div',
          {
            class: 'zendesk-callout zendesk-callout--no',
            'data-test': 'zendesk-match',
            role: 'status',
          },
          [
            h(XCircleIcon, { class: 'zendesk-callout-icon', 'aria-hidden': 'true' }),
            h('span', { class: 'zendesk-callout-text' }, [
              h('strong', `Not order #${props.orderNo}`),
              ` — marked by ${no.rejected_by || 'you'} and noted on the ticket. It will be left out of this order from now on.`,
            ]),
            h(
              'button',
              {
                type: 'button',
                class: 'zendesk-callout-link',
                'data-test': 'zendesk-undo-reject',
                disabled: rejecting.value.has(t.id),
                onClick: (e: Event) => {
                  e.stopPropagation();
                  void undoReject(t);
                },
              },
              rejecting.value.has(t.id) ? 'Undoing…' : 'Undo'
            ),
          ]
        );
      }
      const busy = confirming.value.has(t.id) || rejecting.value.has(t.id);
      return h(
        'div',
        { class: 'zendesk-callout zendesk-callout--ask', 'data-test': 'zendesk-match' },
        [
          h(ExclamationTriangleIcon, { class: 'zendesk-callout-icon', 'aria-hidden': 'true' }),
          h('span', { class: 'zendesk-callout-text' }, [
            // AB#21991 — a ticket number written into the order reference
            // ("TKT# 8924") is a different basis from a match on reference text.
            h(
              'strong',
              t.link?.basis === 'reference'
                ? 'Linked by the ticket number in the order reference.'
                : 'Matched on customer reference only.'
            ),
            t.link?.basis === 'reference'
              ? ` The thread does not name order #${props.orderNo} or carry its tech pack. Is it about this order?`
              : ` Is this ticket about order #${props.orderNo}?`,
          ]),
          h('span', { class: 'zendesk-callout-actions' }, [
            h(
              'button',
              {
                type: 'button',
                class: 'zendesk-deny-btn',
                'data-test': 'zendesk-reject-match',
                disabled: busy,
                title:
                  'Writes a private note on the ticket that it is not this order, and keeps it out of this order from now on',
                onClick: (e: Event) => {
                  e.stopPropagation();
                  void reject(t);
                },
              },
              [
                h(XMarkIcon, { class: 'size-4', 'aria-hidden': 'true' }),
                rejecting.value.has(t.id) ? 'Noting on the ticket…' : 'No, not this order',
              ]
            ),
            h(
              'button',
              {
                type: 'button',
                class: 'zendesk-confirm-btn',
                'data-test': 'zendesk-confirm-match',
                disabled: busy,
                title:
                  'Writes a private note on the ticket naming this order and you, so it is matched by order number from now on',
                onClick: (e: Event) => {
                  e.stopPropagation();
                  void confirm(t);
                },
              },
              [
                h(CheckIcon, { class: 'size-4', 'aria-hidden': 'true' }),
                confirming.value.has(t.id)
                  ? 'Noting on the ticket…'
                  : `Yes, confirm order #${props.orderNo}`,
              ]
            ),
          ]),
        ]
      );
    };

    return () => {
      if (p.loading && p.tickets.length === 0) {
        return h('div', { class: 'py-8 text-center text-sm text-ink-mute' }, 'Loading…');
      }
      if (p.tickets.length === 0) {
        // Nothing at all under a failed lookup: the error banner directly above
        // is the whole of what is known, and a second line saying anything else
        // can only subtract from it.
        return p.failed
          ? null
          : h(
              'div',
              {
                class: 'py-8 text-center text-sm text-ink-mute',
                'data-test': `zendesk-empty-${p.kind}`,
              },
              p.empty
            );
      }
      return h(
        'div',
        p.tickets.map((t) => renderTicket(t))
      );
    };
  },
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
:deep(.zendesk-html) img {
  max-width: 100%;
  height: auto;
}
:deep(.zendesk-html) a {
  color: #009fe3;
  text-decoration: underline;
}
:deep(.zendesk-html) blockquote {
  border-left: 3px solid var(--border);
  padding-left: 0.75rem;
  color: var(--ink-soft);
}
.zd-sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.zd-refresh {
  font-size: 12px;
  padding: 2px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--ink-soft);
  cursor: pointer;
}
.zd-refresh:hover:not(:disabled) {
  background: var(--surface-2);
  color: var(--ink);
}
.zd-refresh:disabled {
  opacity: 0.5;
  cursor: default;
}
.zd-error {
  background: var(--accent-soft);
  border: 1px solid var(--warning);
  color: var(--ink);
}
.zendesk-soon {
  @apply flex flex-col items-center text-center gap-2 py-14 px-6 text-ink-soft;
}
.zendesk-soon-icon {
  @apply w-12 h-12 text-[#25D366] mb-1;
}
.zendesk-soon-title {
  @apply text-base font-semibold text-ink;
}
.zendesk-soon-text {
  @apply text-sm max-w-[42ch];
}
</style>

<style lang="scss">
/*
 * Not scoped on purpose: the ticket list is an inner render-function
 * component, and scoped rules never reach elements it creates — which is
 * how the confirm button first shipped looking like plain text.
 */
.zendesk-callout {
  @apply flex items-center gap-[10px] px-4 py-[8px] text-[12px] leading-[16px] border-t;
}
.zendesk-callout--ask {
  /* Themed amber: the literal cream/brown was dark-on-dark (unreadable) and
     near-white (background) in dark mode. Warning tokens read in both. */
  @apply bg-[var(--warning-soft)] border-[var(--warning)] text-[var(--warning)];
}
.zendesk-callout--ok {
  @apply bg-[var(--success-soft)] border-[#CFE9DF] text-[var(--success)];
}
.zendesk-callout-icon {
  @apply w-[18px] h-[18px] shrink-0;
}
.zendesk-callout-text {
  @apply flex-1 min-w-0;
}
.zendesk-callout--no {
  @apply bg-[#F3F4F6] border-line text-[#4B5563];
}
.zendesk-callout--no .zendesk-callout-icon {
  @apply text-ink-soft;
}
.zendesk-callout-actions {
  @apply flex items-center gap-[8px] shrink-0;
}
.zendesk-callout-link {
  @apply text-[12px] text-primary bg-transparent border-0 p-0 cursor-pointer shrink-0 hover:underline;
}
.zendesk-callout-link:disabled {
  @apply opacity-60 cursor-wait;
}
.zendesk-deny-btn {
  @apply inline-flex items-center gap-[6px] h-[28px] px-[12px] rounded-[6px] text-[12px] font-semibold
         text-[var(--warning)] bg-surface border border-[var(--warning)] cursor-pointer transition-colors whitespace-nowrap shrink-0;
}
.zendesk-deny-btn:hover:not(:disabled) {
  @apply bg-[var(--warning-soft)];
}
.zendesk-deny-btn:disabled {
  @apply opacity-60 cursor-wait;
}
.zendesk-deny-btn:focus-visible {
  @apply outline outline-2 outline-offset-1 outline-[var(--warning)];
}
.zendesk-confirm-btn {
  @apply inline-flex items-center gap-[6px] h-[28px] px-[12px] rounded-[6px] text-[12px] font-semibold
         text-white bg-[var(--accent)] border border-[var(--accent)] cursor-pointer transition-colors whitespace-nowrap shrink-0;
}
.zendesk-confirm-btn:hover:not(:disabled) {
  @apply bg-[var(--accent-strong)] border-[var(--accent-strong)];
}
.zendesk-confirm-btn:disabled {
  @apply opacity-60 cursor-wait;
}
.zendesk-confirm-btn:focus-visible {
  @apply outline outline-2 outline-offset-1 outline-[var(--accent)];
}

/*
 * Rendered email HTML is arbitrary sender markup that assumes a light
 * background — white letterheads, dark body text with no background set. Left
 * bare it bled as a raw white block in dark mode (and a mail that sets only a
 * dark text colour would render dark-on-dark). Pinning it to a contained,
 * light "paper" card — deliberately light in BOTH themes, the way Gmail and
 * Outlook show a message in dark mode — keeps every email legible and looking
 * intentional. `overflow-x-auto` keeps a wide mail from stretching the modal.
 */
.zendesk-html {
  /* Paper: white with dark ink in BOTH themes, on purpose (see above) — the
     sender's inline colours assume white paper, so a themed surface here is
     what made dark mode unreadable (AB#21838). */
  @apply rounded p-3 overflow-x-auto;
  background: #ffffff;
  color: #1b2430;
  border: 1px solid var(--border-soft);
}
</style>
